/**
 * Список вопросов (client-questions): у клиента — «Мои вопросы» организации, у команды — «Вопросы
 * клиентов» по видимости. Фильтр по статусу, у команды — поиск по теме, номеру и клиенту.
 * Сначала вопросы, ждущие ответа, затем по последнему сообщению.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { MessageCircleQuestion } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { questionsApi, teamApi, type QuestionListItem } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/design-system/components/primitives/tabs'
import { Input } from '@/design-system/components/primitives/input'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Pagination } from '@/design-system/components/data/pagination'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { QuestionStatusBadge, formatDateTime } from './question-ui'

export type QuestionsFilter = 'all' | 'open' | 'answered' | 'closed'

const FILTERS: { value: QuestionsFilter; client: string; team: string }[] = [
  { value: 'all', client: 'Все', team: 'Все' },
  { value: 'open', client: 'Ждут ответа', team: 'Ждут ответа' },
  { value: 'answered', client: 'Есть ответ', team: 'Отвечены' },
  { value: 'closed', client: 'Закрытые', team: 'Закрытые' },
]

export function QuestionsList({
  side,
  reloadKey = 0,
  emptyAction,
}: {
  side: 'client' | 'team'
  /** Смена значения — перечитать список (новый вопрос). */
  reloadKey?: number
  emptyAction?: ReactNode
}) {
  const navigate = useNavigate()
  const [filter, setFilter] = useState<QuestionsFilter>('all')
  // Поиск команды: ввод — сразу, запрос — после паузы в наборе.
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<QuestionListItem[] | null>(null)
  const [total, setTotal] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const team = side === 'team'

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const controller = new AbortController()
    setError(null)
    const q = {
      status: filter === 'all' ? undefined : filter,
      q: team && query ? query : undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }
    const load = team
      ? teamApi.questions.list(q, controller.signal)
      : questionsApi.list(q, controller.signal)
    load
      .then((res) => {
        if (controller.signal.aborted) return
        setItems(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить вопросы'))
      })
    return () => controller.abort()
  }, [filter, query, page, team, reloadKey])

  function path(id: string) {
    return team ? `/team/questions/${id}` : `/questions/${id}`
  }

  return (
    <Tabs
      value={filter}
      onValueChange={(v) => {
        setFilter(v as QuestionsFilter)
        setPage(1)
      }}
    >
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <TabsList aria-label="Какие вопросы показать">
          {FILTERS.map((f) => (
            <TabsTrigger key={f.value} value={f.value}>
              {team ? f.team : f.client}
            </TabsTrigger>
          ))}
        </TabsList>
        {team ? (
          <Input
            aria-label="Поиск по теме, номеру или клиенту"
            placeholder="Тема, номер или клиент"
            className="max-w-xs"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        ) : null}
      </div>

      <TabsContent value={filter} className="pt-0">
        {error ? (
          <Alert variant="danger">{error}</Alert>
        ) : items === null ? (
          <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={MessageCircleQuestion}
            title={filter === 'all' && !query ? 'Вопросов пока нет' : 'Таких вопросов нет'}
            description={
              team ? undefined : 'Задайте вопрос — ответ Центрики придёт сюда и на почту'
            }
            action={filter === 'all' ? emptyAction : undefined}
          />
        ) : (
          <>
            <DataTable>
              <DataTableHead>
                <DataTableRow>
                  <DataTableHeaderCell>№</DataTableHeaderCell>
                  {team ? <DataTableHeaderCell>Клиент</DataTableHeaderCell> : null}
                  <DataTableHeaderCell>Тема</DataTableHeaderCell>
                  <DataTableHeaderCell>Заказ</DataTableHeaderCell>
                  <DataTableHeaderCell>Статус</DataTableHeaderCell>
                  <DataTableHeaderCell>Последнее сообщение</DataTableHeaderCell>
                </DataTableRow>
              </DataTableHead>
              <DataTableBody>
                {items.map((q) => (
                  <DataTableRow key={q.id} clickable onClick={() => navigate(path(q.id))}>
                    <DataTableCell className="tabular-nums text-txt-secondary">
                      {q.number}
                    </DataTableCell>
                    {team ? <DataTableCell>{q.clientName}</DataTableCell> : null}
                    <DataTableCell className={q.unread ? 'font-semibold' : undefined}>
                      <span className="inline-flex items-center gap-2">
                        {q.unread ? (
                          <>
                            <span
                              aria-hidden
                              className="size-2 shrink-0 rounded-round bg-red"
                              title="Новый ответ"
                            />
                            <span className="sr-only">Новый ответ:</span>
                          </>
                        ) : null}
                        <span className="break-words">{q.subject}</span>
                      </span>
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {q.orderNumber ?? '—'}
                    </DataTableCell>
                    <DataTableCell>
                      <QuestionStatusBadge status={q.status} side={side} />
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {formatDateTime(q.lastMessageAt)}
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
            <Pagination
              page={page}
              totalPages={pageCount(total)}
              onChange={setPage}
              className="mt-4"
            />
          </>
        )}
      </TabsContent>
    </Tabs>
  )
}
